/** @jsxImportSource react */
import { useState, useEffect } from 'react'

interface Listing {
    id: number
    title: string
    price: number
    currency: string
    city: string
    images: string
    category: string
}

// Sample listings data as fallback
const sampleListings: Record<string, Listing[]> = {
    property: [
        { id: 1, title: 'Modern Villa in Baghdad', price: 450000, currency: 'USD', city: 'Baghdad', images: '["https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400"]', category: 'property' },
        { id: 2, title: 'Luxury Apartment - Erbil', price: 180000, currency: 'USD', city: 'Erbil', images: '["https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400"]', category: 'property' },
        { id: 3, title: 'Family Home in Basra', price: 320000, currency: 'USD', city: 'Basra', images: '["https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400"]', category: 'property' },
        { id: 4, title: 'Downtown Office Space', price: 150000, currency: 'USD', city: 'Baghdad', images: '["https://images.unsplash.com/photo-1560184897-ae75f418493e?w=400"]', category: 'property' },
    ],
    car: [
        { id: 5, title: '2022 Toyota Camry', price: 32000, currency: 'USD', city: 'Baghdad', images: '["https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400"]', category: 'car' },
        { id: 6, title: '2021 BMW X5', price: 58000, currency: 'USD', city: 'Erbil', images: '["https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400"]', category: 'car' },
        { id: 7, title: '2020 Honda Accord', price: 28000, currency: 'USD', city: 'Basra', images: '["https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400"]', category: 'car' },
        { id: 8, title: '2023 Mercedes C-Class', price: 45000, currency: 'USD', city: 'Baghdad', images: '["https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400"]', category: 'car' },
    ]
}

function App() {
    const [listings, setListings] = useState<Listing[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

    const fetchListings = async (category: string) => {
        setLoading(true)
        try {
            const response = await fetch(`/api/listings?category=${category}&limit=20`)
            if (!response.ok) {
                throw new Error('API unavailable')
            }
            const data = await response.json() as { listings: Listing[] }
            if (data.listings && data.listings.length > 0) {
                setListings(data.listings)
                console.log(`Loaded ${data.listings.length} ${category} listings from API`)
            } else {
                // Use sample data as fallback
                setListings(sampleListings[category] || [])
                console.log(`Using sample ${category} listings (API returned no data)`)
            }
        } catch (err) {
            console.error('Error fetching listings, using sample data:', err)
            // Use sample data as fallback
            setListings(sampleListings[category] || [])
        } finally {
            setLoading(false)
        }
    }

    const handleCategoryClick = (category: string) => {
        console.log(`Category clicked: ${category}`)
        setSelectedCategory(category)
        fetchListings(category)
        // Scroll to listings section
        setTimeout(() => {
            window.scrollTo({ top: 800, behavior: 'smooth' })
        }, 100)
    }

    return (
        <div className="app">
            {/* Header */}
            <header className="header">
                <div className="container">
                    <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
                        <img src="/sharray_logo_new.png" alt="Sharray Logo" style={{ height: '40px', width: 'auto' }} />
                        <span className="logo-text">Sharray</span>
                    </a>
                    <nav className="nav">
                        <a href="/smart-pricing.html">Smart Pricing</a>
                        <a href="/login.html">Login</a>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="hero">
                <div className="container">
                    <h1 style={{ color: 'var(--orange-primary)' }}>Sharray | شرّاي</h1>
                    <p style={{ color: '#1F2937', fontSize: '28px', fontWeight: 600, marginBottom: '48px' }}>Buy • Sell • Rent</p>

                    {/* Modern Search Bar */}
                    <div className="hero-search-wrapper">
                        <div className="search-icon-box">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </div>
                        <input type="text" className="search-input-main" placeholder="Search for Properties, Motors, and More" />
                        <button className="search-btn-action">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                        </button>
                    </div>

                    {/* Category Glass Cards */}
                    <div className="category-glass-cards">
                        <div className="glass-card" onClick={() => handleCategoryClick('property')}>
                            <div className="glass-card-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                </svg>
                            </div>
                            <span className="glass-card-title">Properties</span>
                        </div>
                        <div className="glass-card" onClick={() => handleCategoryClick('car')}>
                            <div className="glass-card-icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                                </svg>
                            </div>
                            <span className="glass-card-title">Motors</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Listings Section - Only show when category is selected */}
            {selectedCategory && (
                <section className="section" style={{ background: 'white', paddingTop: '40px' }}>
                    <div className="container">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                            <h2>{selectedCategory === 'property' ? 'Properties' : 'Motors'}</h2>
                            <button
                                onClick={() => window.location.href = '/login.html'}
                                style={{
                                    background: 'white',
                                    border: '2px solid var(--orange-primary)',
                                    color: 'var(--orange-primary)',
                                    padding: '12px 24px',
                                    borderRadius: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '16px'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="4" y1="21" x2="4" y2="14"></line>
                                    <line x1="4" y1="10" x2="4" y2="3"></line>
                                    <line x1="12" y1="21" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12" y2="3"></line>
                                    <line x1="20" y1="21" x2="20" y2="16"></line>
                                    <line x1="20" y1="12" x2="20" y2="3"></line>
                                    <line x1="1" y1="14" x2="7" y2="14"></line>
                                    <line x1="9" y1="8" x2="15" y2="8"></line>
                                    <line x1="17" y1="16" x2="23" y2="16"></line>
                                </svg>
                                Filters
                            </button>
                        </div>
                        {loading ? (
                            <div className="loading-state">Loading listings...</div>
                        ) : (
                            <div className="grid">
                                {listings.map(listing => {
                                    const images = JSON.parse(listing.images)
                                    const imageUrl = images[0] || 'https://via.placeholder.com/300x200'
                                    const price = new Intl.NumberFormat('en-IQ').format(listing.price)

                                    return (
                                        <div key={listing.id} className="card">
                                            <img src={imageUrl} alt={listing.title} className="card-image" />
                                            <div className="card-content">
                                                <span className="category-badge">{listing.category}</span>
                                                <h3>{listing.title}</h3>
                                                <p className="price">{price} {listing.currency}</p>
                                                <p className="location">📍 {listing.city}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-section">
                            <div className="logo">
                                <img src="/sharray_logo_new.png" alt="Sharray Logo" style={{ height: '32px', width: 'auto', marginRight: '8px' }} />
                                <span className="logo-text">Sharray</span>
                            </div>
                            <p>Iraq's trusted marketplace for cars and properties</p>
                        </div>
                        <div className="footer-section">
                            <h4>Quick Links</h4>
                            <a href="/login.html">Download App</a>
                        </div>
                        <div className="footer-section">
                            <h4>Legal</h4>
                            <a href="/privacy-policy.html">Privacy Policy</a>
                            <a href="/about-us.html">About Us</a>
                        </div>
                        <div className="footer-section">
                            <h4>Contact</h4>
                            <p>Email: workshowrok25@gmail.com</p>
                            <p>Phone: 07729004872</p>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© 2025 Sharray. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    )
}

export default App
